import { ReactElement } from "react";
import { IDelayBase, ICasterProduction } from "../../../../types";
import Chart from "../../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../../components/charts/chartUtils";
import { getPalette } from "../../../../configuration/configuration";
import * as d3 from "d3";
import KeyValueItem from "../../../../components/charts/keyValueItem";
import { formatDateTimeWithSeconds, formatTimeWithSeconds } from "../../../../system/utils";
import HtmlTooltip from "../../../../components/charts/tooltip";
import AxisHorizontal from "../../../../components/charts/AxisHorizontal";
import DelayLayerGantt from "../../../interruptions/compoenents/delayLayerGantt";
import { DateTime } from "luxon";


interface IProps {
  productionData: ICasterProduction[];
}

const ProductionChart = ({ productionData }: IProps): ReactElement => {
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: 90,
    marginBottom: 30,
    marginLeft: 9,
    marginRight: 9,
    marginTop: 10
  });

  const chartID = useUniqueId("management-chart");

  const xScale = d3.scaleLinear()
    .domain([productionData[0].beginDatetime, productionData[productionData.length - 1].endDatetime ?? DateTime.now().toSeconds()])
    .range([0, dimensions.boundedWidth]);

  const xAccessorScaled = (d: ICasterProduction) => xScale(d.beginDatetime);
  const widthAccessorScaled = (d: ICasterProduction) => xScale(d.endDatetime ?? DateTime.now().toSeconds()) - xScale(d.beginDatetime);

  const xAccessorDelayScaled = (d: IDelayBase) => xScale(d.beginDatetime);
  const widthAccessorDelayScaled = (d: IDelayBase) => xScale(d.endDatetime ?? DateTime.now().toSeconds()) - xScale(d.beginDatetime);

  const boundedHeight = dimensions.boundedHeight - 2;

  return (
    <div className="flex-grow-0" style={{ height: 140, width: "100%" }}>
      <div ref={ref} style={{ height: 120, width: "100%" }}>
        <Chart dimensions={dimensions}>

          <AxisHorizontal
            chartID={chartID}
            dimensions={dimensions}
            scale={xScale}
            label={"Datetime"}
            ticks={getTicks(xScale.domain(), 5)}
            formatTick={(value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number)}/>


          <rect
            style={{ fill: getPalette().statusColor.empty.background }}
            fillOpacity={0.2}
            x={0}
            y={0}
            width={dimensions.boundedWidth - 1}
            height={boundedHeight} />

          <g>
            { productionData.map((heat, index): ReactElement => {
              const x = xAccessorScaled(heat);
              const w = widthAccessorScaled(heat);

              const middle = w / 2.0 + x;

              return(
                <g key={`${chartID}-${index}`}>

                  <HtmlTooltip title={
                    <div className="display-flex-column align-items-center" >
                      <div className="display-flex-column align-items-center" >
                        <strong>{heat.machine.name}</strong>
                        <strong>{heat.heatNumber}</strong>
                      </div>
                      <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(heat.beginDatetime as number)} />
                      <KeyValueItem name={"End"} value={formatTimeWithSeconds(heat.endDatetime as number)} />
                      <KeyValueItem name={"Steel"} value={heat.steel.code}/>
                      {/*<KeyValueItem name={"Weight"} value={formatNumber(toTons(heat.weight), 2)} unit="US ton" />*/}
                      {/*<KeyValueItem name={"Power ON"} value={formatNumber(heat.powerON, 2)} unit="min" />*/}
                      {/*<KeyValueItem name={"Power OFF"} value={formatNumber(heat.powerOFF, 2)} unit="min" />*/}
                      {/*<KeyValueItem name={"Tap to Tap"} value={formatNumber(heat.tapToTap / 60, 2)} unit="min" />*/}
                    </div>
                  }>
                    <g>
                      <rect
                        style={{ fill: getPalette().statusColor.production.background }}
                        x={x}
                        y={0}
                        width={w}
                        height={boundedHeight} />
                    </g>
                  </HtmlTooltip>

                  <DelayLayerGantt delays={heat.delays} height={boundedHeight} y={0} xAccessorScaled={xAccessorDelayScaled}
                    widthAccessorScaled={widthAccessorDelayScaled} selected={null} chartKey={chartID} />

                  <text x={middle} y={boundedHeight / 2.0 + 5} fontWeight={"bold"} fontSize="1em" textAnchor="middle" fill="white">{heat.heatNumber}</text>
                </g>
              );
            })
            }

          </g>
        </Chart>
      </div>
    </div>
  );
};

export default ProductionChart;
